import React, { useContext, useState } from 'react';
import { useAuth } from '../../routes/components/AuthProvider';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { El } from './style';
import { emailRegex } from '@src/components/FormFields/constants';
import { Form, Label, Button, Dimmer, Divider, Message } from 'semantic-ui-react';
import { FormFields } from '@src/components/FormFields';
import notify from '~/components/Notify';
import AuthService from '~/api/services/auth';
import LayoutAuth from './LayoutAuth';
import { ThemeContext } from 'styled-components';
import LoaderMilkCan from '~/components/Loader';
import InfoPopup from '~/components/InfoPopup';
import { browserCheck, handleErrors } from '~/utils/helpers';
import Edge from '@assets/images/edge.png';
import Firefox from '@assets/images/firefox.png';
import Chrome from '@assets/images/chrome.png';
import Safari from '@assets/images/safari.png';
import AzureSSOButton from '~/components/AzureSSOButton';
interface LoginData {
  email: string;
  password: string;
}

interface AuthenticationPage {
  verifyUser?: boolean;
  resetPassword?: boolean;
  isTfaRequired?: boolean;
}

const BrowserImages = [
  {
    src: Edge,
    link: 'https://www.microsoft.com/en-us/edge',
    version: '>=80',
    name: 'Microsoft Edge'
  },
  {
    src: Firefox,
    link: 'https://www.mozilla.org/firefox/new/',
    version: '>=50',
    name: 'Mozilla Firefox'
  },
  {
    src: Chrome,
    link: 'https://www.google.com/chrome/',
    version: '>=50',
    name: 'Google Chrome'
  },
  {
    src: Safari,
    link: 'https://www.apple.com/safari/',
    version: '>=10',
    name: 'Apple Safari'
  }
]

const AuthenticationPage: React.FC<AuthenticationPage> = (props) => {
  const [isBusy, setIsBusy] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const { handleSubmit, control, reset } = useForm<LoginData | { email: string }>({
    mode: 'onSubmit'
  });
  const { t } = useTranslation();
  const { fetchSystemData, setIsTfaRequired, setForcePassword } = useAuth()
  const {
    logoTitle,
    firstColor,
    headerTitle,
    code,
    blockBrowsers,
    hasMicrosoftAzureLogin
  } = useContext(ThemeContext)
  const blockLoginWrongBrowser = Boolean(blockBrowsers) && !browserCheck()

  const [capsLockOn, setCapsLockOn] = useState(false);

  async function onLoginSubmited(data) {
    setIsBusy(true);
    try {
      const languageStored = localStorage.getItem('i18nextLng')
      const response = await AuthService.loginRequest({ ...data, userLanguage: languageStored || code })
      window.localStorage.isMySessionActive = 'false';
      localStorage.setItem('token', response.data.token)
      localStorage.setItem('tabsOpen', '1');
      if (response.data.tfaRequired) {
        setIsTfaRequired(true, response.data?.tfaType)
        if (response.data.forceChangePassword) {
          setForcePassword(true)
        }
      } else {
        if (!response.data.forceChangePassword) {
          fetchSystemData()
        } else {
          setForcePassword(true)
        }
      }
    } catch (error) {
      if (error.response.data) {
        if (error.response.data.message === 'wrong_email_or_password') {
          notify(t('Wrong email or password'), 'error')
        } else {
          notify(error.response.data.message, 'error');
        }
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const capsLockOn = event.getModifierState('CapsLock');
    setCapsLockOn(capsLockOn);
  }

  function renderAzureSSO() {
    return <>
        <Divider horizontal>{t('Or')}</Divider>
        <Form.Group style={{ justifyContent: 'center', marginTop: 0 }}>
          <Form.Field style={El.loginFormSubmitWrapper}>
           <AzureSSOButton
              size='large'
              fluid
              disabled={isBusy}
            />
          </Form.Field>
        </Form.Group>
    </>
  }

  const renderLoginForm = () => {
    return (
      <Form style={El.loginForm} onSubmit={handleSubmit(onLoginSubmited)}>
        <El.FixedPrice style={{ backgroundImage: `url(${logoTitle})` }} />
        <El.FormLogo style={{ color: firstColor }}>{headerTitle}</El.FormLogo>
        <Divider section />
        <FormFields.Input
          style={{ ...El.loginFormField, marginBottom: '0.5rem' }}
          name='email'
          type='text'
          icon='users'
          iconPosition='left'
          size='large'
          rules={{
            required: {
              value: true,
              message: t('Please enter email')
            }
          }}
          placeholder={t('Email')}
          control={control}
        />

        <FormFields.Input
          style={{ ...El.loginFormField, marginBottom: '0.5rem' }}
          name='password'
          icon='lock'
          type='password'
          iconPosition='left'
          onKeyDown={handleKeyDown}
          size='large'
          rules={{
            required: {
              value: true,
              message: t('Please enter password')
            }
          }}
          placeholder={t('Password')}
          control={control}
        />
        {capsLockOn && <Message color='yellow'>{t('Your caps lock key is active')}</Message>}
        <Form.Field style={El.loginFormRememberFieldWrapper}>
          <Label
            onClick={() => {
              reset();
              setForgotPassword(true);
            }}
            style={{ ...El.loginFormForgotPassword, color: firstColor }}
            basic
          >
            {t('Forgot Password?')}
          </Label>
        </Form.Field>
        <Form.Group style={{ justifyContent: 'end', marginTop: 0 }}>
          <Form.Field style={El.loginFormSubmitWrapper}>
            <Button
              style={{ ...El.loginFormSubmitButton, backgroundColor: `${firstColor}` }}
              primary
              content={t('Login')}
            />
          </Form.Field>
        </Form.Group>
        {hasMicrosoftAzureLogin ? renderAzureSSO() : <Divider/>}
      </Form>
    );
  };

  async function onSubmitResetPassword(data: { email: string }) {
    setIsBusy(true);
    try {
      await AuthService.forgotPassword(data);
      setForgotPassword(false);
      notify(t('We have e-mailed your password reset link.'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderForgotPasswordForm() {
    return (
      <Form style={El.loginForm} onSubmit={handleSubmit(onSubmitResetPassword)}>
        <Dimmer active={isBusy} inverted>
          <LoaderMilkCan isLoading={isBusy} />
        </Dimmer>
        <El.FixedPrice style={{ backgroundImage: `url(${logoTitle})` }} />
        <El.FormLogo style={{ color: firstColor }}>{headerTitle}</El.FormLogo>
        <Divider section />
        <Form.Group style={{ justifyContent: 'center', alignItems: 'baseline' }}>
          <El.FormHeading>
            {t('Reset password')}
          </El.FormHeading>
          <InfoPopup
            content={t('You will receive an email for resetting your password.')}
            styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 5 }}
          />
        </Form.Group>
        <FormFields.Input
          name='email'
          type='text'
          key={'emailForgotPassword'}
          id={'emailForgotPassword'}
          style={{ ...El.loginFormField, marginBottom: '0.5rem' }}
          rules={{
            required: {
              value: true,
              message: t('Please enter email')
            },
            pattern: {
              value: emailRegex,
              message: t('Please enter valid email')
            }
          }}
          placeholder={t('Email')}
          control={control}
        />
        <Form.Group inline style={{ justifyContent: 'center' }}>
          <Form.Button
            style={{ ...El.loginFormSubmitButton, backgroundColor: `${firstColor}` }}
            primary
            onClick={() => setForgotPassword(false)}
            content={t('Return to login')}
          />
          <Form.Button
            style={{ ...El.loginFormSubmitButton, backgroundColor: `${firstColor}` }}
            primary
            content={t('Reset password')}
          />
        </Form.Group>
        <Divider section />
      </Form>
    );
  }

  if (blockLoginWrongBrowser) {
    return <LayoutAuth>
      <Form style={El.loginForm}>
        <El.FixedPrice style={{ backgroundImage: `url(${logoTitle})` }} />
        <El.FormLogo style={{ color: firstColor }}>{headerTitle}</El.FormLogo>
        <div className='field'>{t('The technical requirements for using the platform is the use of a compatible browser. To do this, please click on the corresponding browser icon. \n\
        You will then be redirected to the relevant browser provider website, where you can download the current browser version:')}</div>
        <El.StyledBrowserImgWrapper>
          {BrowserImages.map(browser => {
            return <div key={browser.src}>
              <a href={browser.link} target={'_blank'} rel='noreferrer'>
                <img src={browser.src} />
                <small>{browser.name}</small>
              </a>
            </div>
          })}
        </El.StyledBrowserImgWrapper>
      </Form>
    </LayoutAuth>
  }

  return (
    <LayoutAuth isLoading={isBusy}>
      {forgotPassword ? renderForgotPasswordForm() : renderLoginForm()}
    </LayoutAuth>
  );
};

export default AuthenticationPage;
